import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../../components/SEO';
import ImageModal from '../../components/ImageModal';
import VideoModal from '../../components/VideoModal';
import ShareModal from '../../components/ShareModal';
import UpgradeRequiredModal from '../../components/UpgradeRequiredModal';

import { getImageUrl } from '../../utils/imageUtils';
import { buildProfileCanonicalUrl } from '../../utils/profileUrl';
import { BRAND } from '../../config/brand';

import { useProfileBySlug, useProfilesList } from '../../api/profiles';
import { useEventsList } from '../../api/events';
import { useCreatePost, usePostsList } from '../../api/posts';
import { useProfileLikeCount, useProfileLikeStatus, useToggleProfileLike } from '../../api/profileLikes';
import { useSendMessage, MESSAGE_GOALS } from '../../api/messages';

import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';

import { canShowWebsite, getGalleryLimit, hasFullAccess, hasStandardAccess, upgradeCta } from '../../utils/entitlements';
import {
    formatRub,
    normalizeUrl,
    tryBuildRutubeEmbedUrl,
    tryBuildVkEmbedUrl
} from './utils';

import CoverSection from './components/CoverSection';
import HeaderCard from './components/HeaderCard';
import CategoryTags from './components/CategoryTags';
import BioCard from './components/BioCard';
import PortfolioCard from './components/PortfolioCard';
import VideoSection from './components/VideoSection';
import GallerySection from './components/GallerySection';
import ContactsCard from './components/ContactsCard';
import ConditionsCard from './components/ConditionsCard';
import AudioTracksSection from './components/AudioTracksSection';
import EventsSection from './components/EventsSection';
import PostsSection from './components/PostsSection';
import SimilarProfilesSection from './components/SimilarProfilesSection';
import MessageModal from './components/MessageModal';
import PostCreateModal from './components/PostCreateModal';

const ProfilePage = () => {
    const { mainSlug, subSlug, subSubSlug, username } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { openAuthModal } = useAuthModal();

    const { data, isLoading, error } = useProfileBySlug(username);
    const profile = data?.profile;
    const categoryPath = data?.categoryPath;

    const hasStandard = hasStandardAccess(profile);
    // Product rule: some sections are gated by the PROFILE owner's access (not the viewer).
    const hasFull = hasFullAccess(profile);
    // Viewer permissions (admin/super_admin/full) — used for actions like creating posts.
    const viewerHasFull = hasFullAccess(user);

    const { data: eventsData } = useEventsList(
        profile?.id && hasFull
            ? {
                owner_user_id: profile.id,
                includePast: 1,
                order: 'desc',
                limit: 100,
                page: 1
            }
            : null
    );
    const profileEvents = eventsData?.events || [];

    const { data: postsData } = usePostsList(
        profile?.id && hasFull
            ? {
                owner_user_id: profile.id,
                order: 'desc',
                limit: 100,
                page: 1
            }
            : null
    );
    const profilePosts = postsData?.posts || [];

    const expectedSlugs = useMemo(() => ({
        main: categoryPath?.main?.slug || null,
        sub: categoryPath?.sub?.slug || null,
        subSub: categoryPath?.subSub?.slug || null
    }), [categoryPath]);

    const gotSlugs = useMemo(() => ({
        main: mainSlug || null,
        sub: subSlug || null,
        subSub: subSubSlug || null
    }), [mainSlug, subSlug, subSubSlug]);

    useEffect(() => {
        if (!profile || !expectedSlugs.main) return;

        const profileUsernamePart = profile?.username_slug || profile?.username || null;
        if (profileUsernamePart && username && String(profileUsernamePart) !== String(username)) {
            return;
        }

        const canonicalUsernamePart = profileUsernamePart;
        const mismatch =
            String(gotSlugs.main || '') !== String(expectedSlugs.main || '') ||
            String(gotSlugs.sub || '') !== String(expectedSlugs.sub || '') ||
            String(gotSlugs.subSub || '') !== String(expectedSlugs.subSub || '') ||
            String(username || '') !== String(canonicalUsernamePart || '');

        if (mismatch) {
            const canonicalPath = buildProfileCanonicalUrl({ baseUrl: '', profile, categoryPath });
            navigate(canonicalPath || '/', { replace: true });
        }
    }, [profile, categoryPath, expectedSlugs, gotSlugs, username, navigate]);

    const profileId = profile?.id;
    const { data: likeCountData } = useProfileLikeCount(profileId);
    const { data: likeStatusData } = useProfileLikeStatus(profileId, { enabled: !!user });
    const toggleLike = useToggleProfileLike(profileId);

    const isOwnProfile = !!user && String(user.id) === String(profileId);
    const canCreatePost = isOwnProfile && viewerHasFull;
    const canCreateEvent = isOwnProfile && viewerHasFull;
    const canLike = !!user && !isOwnProfile;
    const canMessage = !!user && !isOwnProfile && hasFull;
    const liked = !!likeStatusData?.liked;
    const likeCount = likeCountData?.count ?? 0;
    const shouldShowMessageButton = !!profileId && !isOwnProfile;

    const canonical = useMemo(() => (
        profile ? buildProfileCanonicalUrl({ baseUrl: BRAND.baseUrl, profile, categoryPath }) : BRAND.baseUrl
    ), [profile, categoryPath]);

    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [activeGalleryUrl, setActiveGalleryUrl] = useState(null);
    const [isVideoOpen, setIsVideoOpen] = useState(false);
    const [activeVideo, setActiveVideo] = useState(null);

    const [isShareOpen, setIsShareOpen] = useState(false);
    const [isMessageOpen, setIsMessageOpen] = useState(false);
    const [messageGoal, setMessageGoal] = useState(MESSAGE_GOALS[0]);
    const [messageBody, setMessageBody] = useState('');

    const ent = useMemo(() => upgradeCta(), []);
    const [upgradeOpen, setUpgradeOpen] = useState(false);

    const sendMessage = useSendMessage();

    // ===== Create post modal (owner-only, Full-only) =====
    const createPost = useCreatePost();
    const [isPostOpen, setIsPostOpen] = useState(false);
    const [postTitle, setPostTitle] = useState('');
    const [postBody, setPostBody] = useState('');
    const [postImageUrl, setPostImageUrl] = useState('');
    const [postLinkUrl, setPostLinkUrl] = useState('');
    const [postVisibility, setPostVisibility] = useState('public');

    useEffect(() => {
        if (!hasFull && isMessageOpen) setIsMessageOpen(false);
    }, [hasFull, isMessageOpen]);

    const videoEmbeds = useMemo(() => (
        Array.isArray(profile?.video_embeds) ? profile.video_embeds : []
    ), [profile?.video_embeds]);

    const videos = useMemo(() => (
        videoEmbeds
            .map((video, idx) => {
                const platform = String(video?.platform || '').toLowerCase();
                const url = normalizeUrl(video?.video_url);
                const embedUrl = platform === 'rutube'
                    ? tryBuildRutubeEmbedUrl(url)
                    : platform === 'vk'
                        ? tryBuildVkEmbedUrl(url)
                        : null;
                const label = platform === 'rutube' ? 'Rutube' : platform === 'vk' ? 'VK Video' : `Video ${idx + 1}`;
                return { id: video?.id || `${idx}`, platform, label, url, embedUrl };
            })
            .filter((video) => video.url)
    ), [videoEmbeds]);

    // ===== Similar profiles ("Похожие профили") =====
    // Must be declared unconditionally (Hooks order). We compute params based on categoryPath.
    const similarParams = useMemo(() => {
        const params = {
            // we only need a small set to pick up to 6 with covers
            limit: 24,
            sort: 'cover'
        };
        if (categoryPath?.main?.id) params.main = categoryPath.main.id;
        if (categoryPath?.sub?.id) params.sub = categoryPath.sub.id;
        if (categoryPath?.subSub?.id) params.subSub = categoryPath.subSub.id;
        return params;
    }, [categoryPath]);

    const { data: similarList } = useProfilesList(similarParams);

    const similarProfiles = useMemo(() => {
        const list = Array.isArray(similarList?.profiles) ? similarList.profiles : [];
        return list
            // exclude current profile
            .filter((p) => String(p.id) !== String(profileId))
            // only with any cover/header image
            .filter((p) => p.cover_url || p.cover_mobile_url)
            .slice(0, 6);
    }, [similarList, profileId]);

    const gallery = Array.isArray(profile?.gallery) ? profile.gallery : [];
    const audioTracks = Array.isArray(profile?.audio_tracks) ? profile.audio_tracks : [];
    const galleryLimit = getGalleryLimit(profile);
    const showWebsite = canShowWebsite(profile);

    const feeLine = useMemo(() => {
        const from = profile?.fee_from_rub;
        const to = profile?.fee_to_rub;
        if (from == null && to == null) return null;
        if (from != null && to != null) return `${formatRub(from)}–${formatRub(to)} ₽`;
        if (from != null) return `от ${formatRub(from)} ₽`;
        return `до ${formatRub(to)} ₽`;
    }, [profile?.fee_from_rub, profile?.fee_to_rub]);

    const links = useMemo(() => ([
        { label: 'ВК', url: normalizeUrl(profile?.social_vk) },
        { label: 'ОК', url: normalizeUrl(profile?.social_ok) },
        { label: 'Max', url: normalizeUrl(profile?.social_max) },
        { label: 'Telegram', url: normalizeUrl(profile?.social_telegram) },
        { label: 'Rutube', url: normalizeUrl(profile?.video_rutube) },
        { label: 'VKvideo', url: normalizeUrl(profile?.video_vk) }
    ].filter((link) => link.url)), [profile]);

    const shareImageSource = profile?.cover_mobile_url || profile?.cover_url || profile?.avatar_url || null;
    const shareImage = shareImageSource ? getImageUrl(shareImageSource) : undefined;

    const locationLine = useMemo(() => {
        const country = String(profile?.country || '').trim() || null;
        const city = String(profile?.location || '').trim() || null;
        return [country, city].filter(Boolean).join(', ') || null;
    }, [profile?.country, profile?.location]);

    const buildCategoryHref = (node) => {
        const slug = node?.slug;
        if (!slug) return '/feed';
        return `/feed?category=${encodeURIComponent(String(slug))}`;
    };

    const breadcrumbItems = [];
    breadcrumbItems.push({ '@type': 'ListItem', position: 1, name: 'Главная', item: `${BRAND.baseUrl}/` });

    if (categoryPath?.main?.name) {
        breadcrumbItems.push({
            '@type': 'ListItem',
            position: breadcrumbItems.length + 1,
            name: categoryPath.main.name,
            item: `${BRAND.baseUrl}${buildCategoryHref(categoryPath.main)}`
        });
    } else {
        breadcrumbItems.push({
            '@type': 'ListItem',
            position: breadcrumbItems.length + 1,
            name: 'Art-каталог',
            item: `${BRAND.baseUrl}/feed`
        });
    }

    if (categoryPath?.sub?.name) {
        breadcrumbItems.push({
            '@type': 'ListItem',
            position: breadcrumbItems.length + 1,
            name: categoryPath.sub.name,
            item: `${BRAND.baseUrl}${buildCategoryHref(categoryPath.sub)}`
        });
    }

    if (categoryPath?.subSub?.name) {
        breadcrumbItems.push({
            '@type': 'ListItem',
            position: breadcrumbItems.length + 1,
            name: categoryPath.subSub.name,
            item: `${BRAND.baseUrl}${buildCategoryHref(categoryPath.subSub)}`
        });
    }

    if (profile) {
        breadcrumbItems.push({
            '@type': 'ListItem',
            position: breadcrumbItems.length + 1,
            name: (profile?.title || '').trim() || profile?.username,
            item: canonical
        });
    }

    const breadcrumbLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems
    };

    const sameAs = links.map((link) => link.url);

    const personLd = {
        '@type': 'Person',
        name: profile?.display_name || profile?.title || profile?.username,
        description: profile?.bio || undefined,
        url: canonical,
        image: profile?.avatar_url ? getImageUrl(profile.avatar_url) : undefined,
        address: locationLine ? { '@type': 'PostalAddress', addressLocality: locationLine } : undefined,
        sameAs: sameAs.length ? sameAs : undefined
    };

    const profilePageLd = {
        '@context': 'https://schema.org',
        '@type': 'ProfilePage',
        name: profile?.title || profile?.username,
        description: profile?.bio || (profile?.username ? `Профиль пользователя ${profile.username} на ${BRAND.name}.` : undefined),
        url: canonical,
        inLanguage: 'ru-RU',
        about: personLd
    };

    const structuredData = [breadcrumbLd, profilePageLd];

    const handleShare = async () => {
        try {
            const url = canonical;
            const title = profile?.title || `@${profile?.username || ''}`;
            const text = profile?.title
                ? `${profile.title} — ${BRAND.name}`
                : profile?.username
                    ? `Профиль @${profile.username} — ${BRAND.name}`
                    : BRAND.name;

            if (navigator?.share) {
                try {
                    await navigator.share({ title, text, url });
                    return;
                } catch (e) {
                    if (String(e?.name || '') === 'AbortError') return;
                }
            }
            setIsShareOpen(true);
        } catch (e) {
            setIsShareOpen(true);
        }
    };

    const handleToggleLike = () => {
        if (!user) {
            openAuthModal('login');
            return;
        }
        if (!canLike) return;
        toggleLike.mutate();
    };

    const handleOpenMessage = () => {
        if (!user) {
            openAuthModal('login');
            return;
        }
        if (!canMessage) return;
        setMessageGoal(MESSAGE_GOALS[0]);
        setMessageBody('');
        setIsMessageOpen(true);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4" />
                    <p className="text-secondary-600">Загрузка профиля...</p>
                </div>
            </div>
        );
    }

    if (error || !profile) {
        return (
            <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-6xl mb-4">😔</div>
                    <h2 className="text-2xl font-semibold text-secondary-900 mb-2">Профиль не найден</h2>
                    <p className="text-secondary-600 mb-4">Пользователь не существует.</p>
                    <button onClick={() => navigate('/feed')} className="btn btn-primary">К ленте</button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-secondary-50">
            <SEO
                title={profile.title ? `${profile.title} — Профиль` : profile.username ? `${profile.username} — Профиль` : 'Профиль'}
                description={
                    profile?.bio ||
                    (profile?.username ? `Профиль пользователя ${profile.username} на ${BRAND.name}.` : `Профиль пользователя на ${BRAND.name}.`)
                }
                categoryPath={categoryPath}
                url={canonical}
                canonical={canonical}
                type="profile"
                image={shareImage}
                structuredData={structuredData}
            />

            <div className="max-w-5xl mx-auto px-4 py-6">
                <div className="mb-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center text-secondary-600 hover:text-secondary-900 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Назад
                    </button>
                </div>

                <div className="rounded-2xl overflow-hidden bg-secondary-100 shadow-soft border border-secondary-200">
                    <CoverSection
                        desktopCoverUrl={profile?.cover_url || null}
                        mobileCoverUrl={profile?.cover_mobile_url || null}
                        coverVideoUrl={hasFull ? profile?.cover_video_url || null : null}
                    />
                </div>

                <div className="-mt-10 relative z-10">
                    <div className="rounded-2xl bg-white border border-secondary-200 shadow-soft p-5">
                        <HeaderCard
                            profile={profile}
                            profileTitle={profile.title}
                            categoryPath={categoryPath}
                            hasStandard={hasStandard}
                            buildCategoryHref={buildCategoryHref}
                            onShare={handleShare}
                            liked={liked}
                            likeCount={likeCount}
                            onToggleLike={handleToggleLike}
                            likeDisabled={isOwnProfile || toggleLike.isLoading}
                            canLike={canLike}
                            shouldShowMessageButton={shouldShowMessageButton}
                            hasFull={hasFull}
                            canMessage={canMessage}
                            onOpenMessage={handleOpenMessage}
                            user={user}
                            canCreatePost={canCreatePost}
                            onCreatePost={() => {
                                setPostTitle('');
                                setPostBody('');
                                setPostImageUrl('');
                                setPostLinkUrl('');
                                setPostVisibility('public');
                                setIsPostOpen(true);
                            }}
                            canCreateEvent={canCreateEvent}
                        />

                        <CategoryTags
                            categoryPath={categoryPath}
                            hasStandard={hasStandard}
                            tags={profile?.tags || []}
                            buildCategoryHref={buildCategoryHref}
                        />
                    </div>
                </div>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-8 space-y-6">
                        {profile?.bio && <BioCard bio={profile.bio} />}
                        {profile?.portfolio_text && <PortfolioCard text={profile.portfolio_text} />}
                        {videos.length > 0 && (
                            <VideoSection
                                videos={videos}
                                onVideoSelect={(video) => {
                                    setActiveVideo(video);
                                    setIsVideoOpen(true);
                                }}
                            />
                        )}
                        {hasStandard && gallery.length > 0 && (
                            <GallerySection
                                gallery={gallery}
                                galleryLimit={galleryLimit}
                                onSelectImage={(url) => {
                                    setActiveGalleryUrl(url);
                                    setIsGalleryOpen(true);
                                }}
                            />
                        )}

                        {/* Posts links (Hauptspalte) */}
                        {hasFull && profilePosts.length > 0 && (
                            <PostsSection posts={profilePosts} />
                        )}
                    </div>

                    <div className="lg:col-span-4 space-y-6">
                        {(profile?.display_name || locationLine || profile?.website || profile?.phone || links.length > 0) && (
                            <ContactsCard
                                profile={profile}
                                locationLine={locationLine}
                                links={links}
                                showWebsite={showWebsite}
                                onUpgradeClick={() => setUpgradeOpen(true)}
                            />
                        )}

                        {(profile?.composition || feeLine) && (
                            <ConditionsCard profile={profile} feeLine={feeLine} />
                        )}

                        {hasStandard && audioTracks.length > 0 && (
                            <AudioTracksSection audioTracks={audioTracks} />
                        )}

                        {/* Events rechts (Sidebar) */}
                        {hasFull && profileEvents.length > 0 && (
                            <EventsSection events={profileEvents} />
                        )}
                    </div>
                </div>

                {similarProfiles.length > 0 && (
                    <div className="mt-10">
                        <SimilarProfilesSection profiles={similarProfiles} />
                    </div>
                )}

                <ImageModal
                    isOpen={isGalleryOpen}
                    onClose={() => {
                        setIsGalleryOpen(false);
                        setActiveGalleryUrl(null);
                    }}
                    imageUrl={activeGalleryUrl || ''}
                    imageAlt="gallery"
                />

                <VideoModal
                    isOpen={isVideoOpen}
                    onClose={() => {
                        setIsVideoOpen(false);
                        setActiveVideo(null);
                    }}
                    video={activeVideo}
                />

                <ShareModal
                    isOpen={isShareOpen}
                    onClose={() => setIsShareOpen(false)}
                    url={canonical}
                    title={profile?.title || `@${profile?.username || ''}`}
                    text={
                        profile?.title
                            ? `${profile.title} — ${BRAND.name}`
                            : profile?.username ? `Профиль @${profile.username} — ${BRAND.name}` : BRAND.name
                    }
                />

                <UpgradeRequiredModal
                    isOpen={upgradeOpen}
                    onClose={() => setUpgradeOpen(false)}
                    title={ent.title}
                    body={ent.body}
                    ctaHref={ent.href}
                    ctaText={ent.primaryText}
                />

                <MessageModal
                    isOpen={isMessageOpen}
                    hasFull={hasFull}
                    profile={profile}
                    messageGoal={messageGoal}
                    setMessageGoal={setMessageGoal}
                    messageBody={messageBody}
                    setMessageBody={setMessageBody}
                    onClose={() => setIsMessageOpen(false)}
                    sendMessage={sendMessage}
                    profileId={profileId}
                />

                <PostCreateModal
                    isOpen={isPostOpen}
                    onClose={() => setIsPostOpen(false)}
                    createPost={createPost}
                    title={postTitle}
                    setTitle={setPostTitle}
                    body={postBody}
                    setBody={setPostBody}
                    imageUrl={postImageUrl}
                    setImageUrl={setPostImageUrl}
                    linkUrl={postLinkUrl}
                    setLinkUrl={setPostLinkUrl}
                    visibility={postVisibility}
                    setVisibility={setPostVisibility}
                    onCreated={() => {
                        setIsPostOpen(false);
                        // refresh profile posts list
                        try {
                            // usePostsList uses react-query: invalidations are done inside useCreatePost
                            // but this ensures UI is fresh even with keepPreviousData
                        } catch (_) { }
                    }}
                />
            </div>
        </div>
    );
};

export default ProfilePage;
